package com.milta.web

import androidx.compose.runtime.Composable
import com.milta.core.localization.WebImportRouteStrings
import com.milta.feature.route.domain.entity.Delivery
import com.milta.feature.route.domain.entity.RouteBatchDriverProposal
import com.milta.feature.route.domain.entity.RouteBatchResult
import com.milta.feature.route.presentation.WebImportRouteIntent
import com.milta.feature.route.presentation.WebImportRouteState
import com.milta.feature.team.domain.entity.Driver
import org.jetbrains.compose.web.css.height
import org.jetbrains.compose.web.css.padding
import org.jetbrains.compose.web.css.percent
import org.jetbrains.compose.web.css.px
import org.jetbrains.compose.web.css.width
import org.jetbrains.compose.web.dom.Button
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.Text

/** Only the first 4 stops of each driver's route are previewed on its card — matches the ticket's
 * mockup intent, avoids an unbounded list for a large batch. */
private const val MAX_PREVIEW_STOPS = 4

/**
 * Écran 3 (MIL-51) — répartition du batch avant envoi. One card per driver (avatar/initials, name,
 * distance/duration-or-"non calculée", stops count pastille, first [MAX_PREVIEW_STOPS] stops), then
 * a single "Valider et envoyer les tournées" action (`POST /route-batches/{id}/dispatch`) — no
 * re-optimization button from this step, out of MIL-51's scope. Mirrors the mobile
 * `RouteBatchPreviewScreen`'s `DriverProposalCard` (shared/commonMain), reimplemented with Compose
 * HTML primitives (this module targets Kotlin/Wasm-JS, not Compose Multiplatform Material) rather
 * than reused directly.
 */
@Composable
internal fun WebRouteBatchResultScreen(
    state: WebImportRouteState,
    strings: WebImportRouteStrings,
    onIntent: (WebImportRouteIntent) -> Unit,
) {
    val result = state.batchResult ?: return

    val dispatchError = state.submitError
    Div {
        if (dispatchError != null) {
            Div(attrs = { style { property("margin-bottom", "14px") } }) {
                InlineBanner(kind = InlineBannerKind.Error, text = mapDispatchError(dispatchError, strings))
            }
        }

        Div(
            attrs = { style { property("font-size", "13.5px"); property("font-weight", "700"); property("color", WebTextPrimary); property("margin-bottom", "16px") } },
        ) { Text(buildResultHeader(result, strings)) }

        result.drivers.forEach { proposal ->
            DriverResultCard(
                proposal = proposal,
                driver = state.drivers.firstOrNull { it.id == proposal.driverAccountId },
                stops = state.batchStopsByRouteId[proposal.routeId].orEmpty(),
                isLoadingStops = state.isLoadingBatchStops,
                hasStopsLoadError = proposal.routeId in state.batchStopsFailedRouteIds,
                strings = strings,
            )
            Div(attrs = { style { height(12.px) } })
        }

        // Mirrors WebVerifyAssignRouteScreen's SubmitButton: once dispatch has terminally failed with
        // "already_dispatched" (409 — the batch is already sent, retrying is a no-op that would just
        // re-show the same banner), the CTA stays disabled instead of inviting another click.
        val dispatchDisabled = state.isDispatchingBatch || dispatchError == "already_dispatched"
        Button(
            attrs = {
                if (!dispatchDisabled) onClick { onIntent(WebImportRouteIntent.ValidateAndSend) }
                style {
                    gradientButtonStyle(this)
                    width(100.percent)
                    if (dispatchDisabled) {
                        property("opacity", "0.5")
                        property("cursor", "not-allowed")
                        property("box-shadow", "none")
                    }
                }
            },
        ) { Text(strings.resultDispatchButton) }
    }
}

private fun buildResultHeader(result: RouteBatchResult, strings: WebImportRouteStrings): String {
    val driverWord = if (result.totalDrivers == 1) strings.resultDriverSingularSuffix else strings.resultDriverPluralSuffix
    return "${result.totalDeliveries} ${strings.resultHeaderMiddle} ${result.totalDrivers} $driverWord"
}

private fun mapDispatchError(code: String, strings: WebImportRouteStrings): String = when (code) {
    "already_dispatched" -> strings.alreadyDispatchedBanner
    "route_not_found" -> strings.routeNotFoundError
    "optimization_unavailable" -> strings.submitOptimizationUnavailableError
    else -> strings.submitServerError
}

@Composable
private fun DriverResultCard(
    proposal: RouteBatchDriverProposal,
    driver: Driver?,
    stops: List<Delivery>,
    isLoadingStops: Boolean,
    hasStopsLoadError: Boolean,
    strings: WebImportRouteStrings,
) {
    Div(
        attrs = { style { property("background", WebSurface); property("border", "1px solid $WebBorder"); property("border-radius", "12px"); padding(16.px) } },
    ) {
        Div(attrs = { style { property("display", "flex"); property("align-items", "center"); property("gap", "12px") } }) {
            Div(
                attrs = {
                    style {
                        width(40.px); height(40.px); property("border-radius", "10px"); property("flex-shrink", "0")
                        property("background", WebActiveTint)
                        property("display", "flex"); property("align-items", "center"); property("justify-content", "center")
                        property("color", WebPrimary); property("font-size", "13.5px"); property("font-weight", "700")
                    }
                },
            ) { Text(driverInitials(driver)) }

            Div(
                attrs = { style { property("flex", "1"); property("font-size", "13.5px"); property("font-weight", "700"); property("color", WebTextPrimary) } },
            ) { Text(driver?.let { "${it.firstName} ${it.lastName}" } ?: proposal.driverAccountId) }

            Div(
                attrs = {
                    style {
                        property("background", WebActiveTint); property("border-radius", "999px")
                        padding(4.px, 10.px)
                        property("font-size", "11px"); property("font-weight", "700"); property("color", WebPrimary)
                    }
                },
            ) { Text("${proposal.deliveryCount} ${strings.resultStopsChipSuffix}") }
        }

        Div(attrs = { style { height(12.px) } })

        Div(attrs = { style { property("display", "flex"); property("gap", "24px"); property("padding-left", "52px") } }) {
            MetricColumn(
                label = strings.resultDistanceLabel,
                value = proposal.distanceKm?.let { "${formatOneDecimal(it)} km" } ?: strings.resultMetricNotCalculatedLabel,
            )
            MetricColumn(
                label = strings.resultDurationLabel,
                value = proposal.durationMinutes?.let { formatDuration(it) } ?: strings.resultMetricNotCalculatedLabel,
            )
        }

        Div(attrs = { style { height(10.px) } })

        Div(attrs = { style { property("padding-left", "52px"); property("display", "flex"); property("flex-direction", "column"); property("gap", "4px") } }) {
            if (isLoadingStops) {
                Div(attrs = { style { property("font-size", "12.5px"); property("color", WebTextSecondary) } }) { Text("…") }
            } else if (hasStopsLoadError) {
                // Distinguishes "the fetch failed" from "this route genuinely has few/no stops" —
                // the stops-count pill and metrics above come from the batch-create response and
                // stay correct regardless, only this preview list is affected.
                Div(attrs = { style { property("font-size", "12.5px"); property("color", WebTextSecondary) } }) {
                    Text(strings.resultStopsLoadError)
                }
            } else {
                stops.take(MAX_PREVIEW_STOPS).forEach { stop ->
                    Div(attrs = { style { property("font-size", "12.5px"); property("color", WebTextPrimary) } }) {
                        Text("${stop.recipientName} — ${stop.address}")
                    }
                }
                // Based on the actually fetched [stops], not `proposal.deliveryCount` — the latter
                // comes from the batch-create response and would overstate "remaining" whenever
                // fewer stops were fetched than the route actually has.
                val remaining = stops.size - MAX_PREVIEW_STOPS
                if (remaining > 0) {
                    Div(attrs = { style { property("font-size", "12.5px"); property("font-weight", "600"); property("color", WebTextSecondary) } }) {
                        Text(strings.resultMoreStopsTemplate.replace("%d", remaining.toString()))
                    }
                }
            }
        }
    }
}

@Composable
private fun MetricColumn(label: String, value: String) {
    Div(attrs = { style { property("display", "flex"); property("flex-direction", "column") } }) {
        Div(attrs = { style { property("font-size", "13.5px"); property("font-weight", "700"); property("color", WebTextPrimary) } }) { Text(value) }
        Div(attrs = { style { property("font-size", "11.5px"); property("color", WebTextSecondary) } }) { Text(label) }
    }
}

private fun driverInitials(driver: Driver?): String {
    if (driver == null) return "?"
    val first = driver.firstName.firstOrNull()?.uppercaseChar() ?: ' '
    val last = driver.lastName.firstOrNull()?.uppercaseChar() ?: ' '
    return "$first$last".trim()
}

// String.format/"%.1f" is JVM-only — not available on Kotlin/Wasm-JS (this module's target). Mirrors
// RouteBatchPreviewScreen's own formatOneDecimal (shared/commonMain), reimplemented locally since
// that one is `private` to its file.
private fun formatOneDecimal(value: Double): String {
    val tenths = kotlin.math.round(value * 10).toLong()
    return "${tenths / 10}.${kotlin.math.abs(tenths % 10)}"
}

private fun formatDuration(minutes: Int): String {
    val h = minutes / 60
    val m = minutes % 60
    return if (h > 0) "${h}h${m.toString().padStart(2, '0')}" else "${m} min"
}
